/**
 * XRHelper - WebXR utilities and helpers
 */

import { Logger } from './Logger';
import { Scene } from '@babylonjs/core/scene';

const logger = Logger.getLogger('XRHelper');
import { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import { WebXRState } from '@babylonjs/core/XR/webXRTypes';
import type { IAnalyticsService } from '../services/interfaces';

export class XRHelper {
  private scene: Scene;
  private xrExperience: WebXRDefaultExperience | null = null;
  public analyticsService?: IAnalyticsService; // Injected from main.ts
  public onVRStateChanged?: (isInVR: boolean) => void; // Callback for VR state changes

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /**
   * Initialize WebXR with default experience
   */
  async initializeXR(): Promise<WebXRDefaultExperience> {
    try {
      // Create default XR experience WITHOUT default UI (we have our own)
      this.xrExperience = await this.scene.createDefaultXRExperienceAsync({
        // Floor mesh options
        floorMeshes: [],

        // Disable teleportation by default (we'll add custom locomotion)
        disableTeleportation: false,

        // Options for the experience helper
        optionalFeatures: true,

        // Disable the default UI completely - we have our own custom button
        disableDefaultUI: true,

        // Enable DOM overlay for HTML UI in VR
        uiOptions: {
          sessionMode: 'immersive-vr',
          referenceSpaceType: 'local-floor',
        },
      });

      logger.info('WebXR initialized successfully (default UI disabled)');

      // Setup XR state change handlers
      this.setupXRStateHandlers();

      return this.xrExperience;

    } catch (error) {
      logger.error('Failed to initialize WebXR:', error);
      throw error;
    }
  }

  /**
   * Setup handlers for XR state changes
   */
  private setupXRStateHandlers(): void {
    if (!this.xrExperience) return;

    const xr = this.xrExperience.baseExperience;

    xr.onStateChangedObservable.add((state) => {
      switch (state) {
        case WebXRState.ENTERING_XR:
          logger.debug('Entering VR mode...');
          this.analyticsService?.trackVRStateChanged('entering');
          this.onEnterXR();
          break;
        case WebXRState.EXITING_XR:
          logger.debug('Exiting VR mode...');
          this.analyticsService?.trackVRStateChanged('exiting');
          this.onExitXR();
          break;
        case WebXRState.IN_XR:
          logger.info('Now in VR mode');
          this.analyticsService?.trackVRStateChanged('in');
          this.analyticsService?.trackVRSessionStarted();
          break;
        case WebXRState.NOT_IN_XR:
          logger.debug('Not in VR mode');
          this.analyticsService?.trackVRStateChanged('not_in');
          this.analyticsService?.trackVRSessionEnded();
          break;
      }
    });
  }

  /**
   * Called when entering VR
   */
  private onEnterXR(): void {
    // VR mode entered - clean minimal experience
    logger.debug('Entered VR mode');
    this.onVRStateChanged?.(true);
  }

  /**
   * Called when exiting VR
   */
  private onExitXR(): void {
    // VR mode exited
    logger.debug('Exited VR mode');
    this.onVRStateChanged?.(false);
  }

  /**
   * Check if WebXR is supported
   */
  static async isXRSupported(): Promise<boolean> {
    if (!navigator.xr) {
      return false;
    }

    try {
      return await navigator.xr.isSessionSupported('immersive-vr');
    } catch {
      return false;
    }
  }

  /**
   * Get the XR experience
   */
  getXRExperience(): WebXRDefaultExperience | null {
    return this.xrExperience;
  }

  /**
   * Check if currently in VR mode
   */
  isInVR(): boolean {
    return this.xrExperience?.baseExperience.state === WebXRState.IN_XR;
  }

  /**
   * Enter VR mode
   */
  async enterVR(): Promise<void> {
    if (!this.xrExperience) {
      throw new Error('XR not initialized');
    }

    try {
      await this.xrExperience.baseExperience.enterXRAsync(
        'immersive-vr',
        'local-floor'
      );
    } catch (error) {
      logger.error('Failed to enter VR:', error);
      throw error;
    }
  }

  /**
   * Exit VR mode
   */
  async exitVR(): Promise<void> {
    if (!this.xrExperience) {
      return;
    }

    try {
      await this.xrExperience.baseExperience.exitXRAsync();
    } catch (error) {
      logger.error('Failed to exit VR:', error);
    }
  }

  /**
   * Configure teleportation with floor meshes
   * @param floorMeshNames - Array of mesh names to use as teleportation targets
   */
  configureFloorMeshes(floorMeshNames: string[]): void {
    if (!this.xrExperience?.teleportation) {
      logger.warn('Teleportation not available - XR not initialized or teleportation disabled');
      return;
    }

    // Find meshes by name and add them as floor meshes
    floorMeshNames.forEach((meshName) => {
      const mesh = this.scene.getMeshByName(meshName);
      if (mesh) {
        this.xrExperience!.teleportation.addFloorMesh(mesh);
        logger.debug(`Added floor mesh for teleportation: ${meshName}`);
      } else {
        logger.warn(`Floor mesh not found: ${meshName}`);
      }
    });
  }

  /**
   * Add a specific mesh as a teleportation floor
   */
  addFloorMesh(meshName: string): void {
    if (!this.xrExperience?.teleportation) {
      logger.warn('Teleportation not available');
      return;
    }

    const mesh = this.scene.getMeshByName(meshName);
    if (mesh) {
      this.xrExperience.teleportation.addFloorMesh(mesh);
      logger.debug(`Added floor mesh: ${meshName}`);
    }
  }

  /**
   * Enable/disable teleportation parabolic ray visual
   */
  setParabolicRayEnabled(enabled: boolean): void {
    if (!this.xrExperience?.teleportation) {
      return;
    }

    // The teleportation feature shows a ray when pointing - this is controlled by the teleportation feature
    // We can't easily toggle the ray visibility, but we can disable teleportation entirely
    logger.debug(`Parabolic ray enabled: ${enabled}`);
  }

  /**
   * Enable/disable snap turn rotation
   */
  setSnapTurnEnabled(enabled: boolean, angle: number = 45): void {
    if (!this.xrExperience?.baseExperience) {
      return;
    }

    // Note: Snap turn would typically be implemented via WebXRControllerMovement feature
    // For now, log the intent - full implementation would require adding the movement feature
    logger.debug(`Snap turn ${enabled ? 'enabled' : 'disabled'}, angle: ${angle}°`);
  }

  /**
   * Get teleportation feature if available
   */
  getTeleportation() {
    return this.xrExperience?.teleportation || null;
  }

  /**
   * Dispose XR resources
   */
  dispose(): void {
    if (this.xrExperience) {
      this.xrExperience.dispose();
      this.xrExperience = null;
    }
  }
}
